'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Quote, Star, ChevronLeft, ChevronRight, Rocket, TrendingUp, Crown, Lightbulb } from 'lucide-react';

interface Testimonial {
  id: number;
  quote: string;
  highlight: string; // Key phrase to emphasize
  author: string;
  role: string;
  type: 'builder' | 'investor';
  rating: number;
  metric: {
    value: string;
    label: string;
  };
  deal?: string; // The engagement they completed
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    quote:
      "Purple didn't just help us find investors—they helped us become investable. The expert guidance transformed how we think about our business model and go-to-market strategy.",
    highlight: "become investable",
    author: 'Sarah Chen',
    role: 'Founder & CEO, TechVenture AI',
    type: 'builder',
    rating: 5,
    metric: {
      value: '$2.4M',
      label: 'Seed Round Raised',
    },
    deal: 'GTM Strategy Capsule',
  },
  {
    id: 2,
    quote:
      "The diligence support alone is worth it. Having comprehensive analysis prepared before I commit has saved me from two bad deals and accelerated three good ones.",
    highlight: "saved me from two bad deals",
    author: 'Michael Torres',
    role: 'Angel Investor, Portfolio of 20+',
    type: 'investor',
    rating: 5,
    metric: {
      value: '3x',
      label: 'Better Deal Quality',
    },
    deal: 'Due Diligence Package',
  },
];

const typeConfig = {
  builder: {
    gradient: 'from-purple-600 to-violet-600',
    bg: 'bg-purple-50',
    border: 'border-purple-100',
    text: 'text-purple-600',
    icon: <Rocket className="w-5 h-5" />,
    label: 'Founder',
  },
  investor: {
    gradient: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    text: 'text-amber-600',
    icon: <TrendingUp className="w-5 h-5" />,
    label: 'Investor',
  },
};

// Stacked card component
function TestimonialCard({
  testimonial,
  position,
  totalCards,
  onPrev,
  onNext,
}: {
  testimonial: Testimonial;
  position: number; // 0 = front, 1 = behind, 2 = further back, etc.
  totalCards: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const config = typeConfig[testimonial.type];

  // Calculate transform based on position
  const getTransform = () => {
    if (position === 0) {
      return 'translateY(0) scale(1) rotateX(0)';
    }
    const yOffset = position * 20;
    const scale = 1 - position * 0.05;
    return `translateY(${yOffset}px) scale(${scale})`;
  };

  const getOpacity = () => {
    if (position === 0) return 1;
    if (position === 1) return 0.7;
    if (position === 2) return 0.4;
    return 0;
  };

  const getZIndex = () => totalCards - position;

  // Highlight the key phrase in the quote
  const renderQuote = () => {
    const parts = testimonial.quote.split(testimonial.highlight);
    if (parts.length === 1) return testimonial.quote;

    return (
      <>
        {parts[0]}
        <span className={cn('font-semibold', config.text)}>
          {testimonial.highlight}
        </span>
        {parts[1]}
      </>
    );
  };

  return (
    <div
      className={cn(
        'absolute inset-0 transition-all duration-500 ease-out',
        position > 2 && 'pointer-events-none'
      )}
      style={{
        transform: getTransform(),
        opacity: getOpacity(),
        zIndex: getZIndex(),
      }}
    >
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 lg:p-10 h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          {/* Type badge */}
          <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-full', config.bg, config.border, 'border')}>
            {config.icon}
            <span className={cn('font-semibold text-sm', config.text)}>
              {config.label}
            </span>
          </div>

          {/* Rating */}
          <div className="flex gap-0.5">
            {Array.from({ length: testimonial.rating }).map((_, i) => (
              <Star
                key={i}
                className="w-4 h-4 fill-amber-400 text-amber-400"
              />
            ))}
          </div>
        </div>

        {/* Quote */}
        <div className="relative mb-8">
          <Quote className={cn('absolute -left-2 -top-2 w-8 h-8 opacity-20', config.text)} />
          <blockquote className="text-xl lg:text-2xl text-gray-700 leading-relaxed font-serif pl-6">
            "{renderQuote()}"
          </blockquote>
        </div>

        {/* Footer */}
        <div className="flex items-end justify-between mt-auto">
          {/* Author */}
          <div className="flex items-center gap-4">
            {/* Avatar with gradient initials */}
            <div
              className={cn(
                'w-14 h-14 rounded-xl flex items-center justify-center text-white font-display font-bold text-lg bg-gradient-to-br',
                config.gradient
              )}
            >
              {testimonial.author.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{testimonial.author}</p>
              <p className="text-sm text-gray-500">{testimonial.role}</p>
            </div>
          </div>

          {/* Metric */}
          <div className="text-right">
            <p
              className={cn(
                'text-3xl font-display font-bold bg-gradient-to-r bg-clip-text text-transparent',
                config.gradient
              )}
            >
              {testimonial.metric.value}
            </p>
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              {testimonial.metric.label}
            </p>
          </div>
        </div>

        {/* Deal badge */}
        {testimonial.deal && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 uppercase tracking-wider">Engagement:</span>
              <span className={cn('text-sm font-medium', config.text)}>
                {testimonial.deal}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Navigation dots
function NavigationDots({
  total,
  current,
  onChange,
}: {
  total: number;
  current: number;
  onChange: (index: number) => void;
}) {
  return (
    <div className="flex items-center justify-center gap-3">
      {Array.from({ length: total }).map((_, index) => {
        const config = typeConfig[testimonials[index].type];
        return (
          <button
            key={index}
            onClick={() => onChange(index)}
            className={cn(
              'transition-all duration-300 rounded-full',
              index === current
                ? `w-10 h-3 bg-gradient-to-r ${config.gradient}`
                : 'w-3 h-3 bg-gray-200 hover:bg-gray-300'
            )}
          />
        );
      })}
    </div>
  );
}

export default function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-advance
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [isVisible]);

  const goToPrev = () => {
    setActiveIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  // Reorder testimonials so active is first
  const orderedTestimonials = [...testimonials.slice(activeIndex), ...testimonials.slice(0, activeIndex)];

  return (
    <section
      ref={sectionRef}
      className="relative py-16 lg:py-20 overflow-hidden"
    >
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, hsl(var(--warm-white)) 0%, hsl(270 60% 99%) 50%, hsl(var(--warm-white)) 100%)',
        }}
      />

      {/* Decorative checkmarks */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg
          className="absolute top-10 left-10 w-32 h-32 opacity-[0.03]"
          viewBox="0 0 100 100"
          fill="none"
        >
          <path
            d="M20 50 L40 70 L80 30"
            stroke="hsl(271 81% 56%)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <svg
          className="absolute bottom-10 right-10 w-48 h-48 opacity-[0.03]"
          viewBox="0 0 100 100"
          fill="none"
        >
          <path
            d="M20 50 L40 70 L80 30"
            stroke="hsl(271 81% 56%)"
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div
            className={cn(
              'transition-all duration-700',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <p className="text-sm font-semibold text-purple-600 uppercase tracking-wider mb-4">
              Success Stories
            </p>
            <h2
              className="font-display font-semibold text-gray-900 mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Real Results from{' '}
              <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                Real Participants
              </span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Hear from founders and investors who've experienced the Purple difference.
            </p>
          </div>
        </div>

        {/* Stacked cards */}
        <div
          className={cn(
            'relative transition-all duration-700 delay-200',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          {/* Cards container */}
          <div className="relative h-[480px] lg:h-[420px] mx-auto max-w-3xl">
            {orderedTestimonials.map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                position={index}
                totalCards={testimonials.length}
                onPrev={goToPrev}
                onNext={goToNext}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-8 mt-8">
            <button
              onClick={goToPrev}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-purple-600 hover:border-purple-200 hover:shadow-lg transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <NavigationDots
              total={testimonials.length}
              current={activeIndex}
              onChange={setActiveIndex}
            />

            <button
              onClick={goToNext}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-purple-600 hover:border-purple-200 hover:shadow-lg transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Trust metrics */}
        <div
          className={cn(
            'mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 transition-all duration-700 delay-400',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          {[
            { value: '98%', label: 'Satisfaction Rate' },
            { value: '4.9', label: 'Average Rating' },
            { value: '500+', label: 'Engagements' },
            { value: '0', label: 'Unresolved Disputes' },
          ].map((stat, i) => (
            <div
              key={i}
              className="text-center p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
            >
              <p className="text-3xl font-display font-bold text-gray-900">
                {stat.value}
              </p>
              <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
